// ---------------------------------------------------------------------------
// ParticipantSearchPanel — Filter meeting data entries by participant.
//
// Compliance/admin tool for MeetingData datasets. Searches every entry that
// contains a given participant (by email or alias) via
// GET /datasets/{dataSetId}/participants/data, and — for owners — exposes a
// "Hard Delete All by Participant" purge backed by the delete endpoint.
//
// The panel owns the search/pagination state and reports the current result
// list to the parent through `onResultsChange` so the parent can swap the
// entries table's data source.
// ---------------------------------------------------------------------------

import { Body1, Button, Input, Text } from '@fluentui/react-components';
import { Delete16Regular, Search16Regular } from '@fluentui/react-icons';
import { useCallback, useState } from 'react';

import {
  PARTICIPANT_HARD_DELETE_ALL_LABEL,
  PARTICIPANT_SEARCH_BUTTON_BUSY_LABEL,
  PARTICIPANT_SEARCH_BUTTON_LABEL,
  PARTICIPANT_SEARCH_CLEAR_LABEL,
  PARTICIPANT_SEARCH_LOAD_MORE_BUSY_LABEL,
  PARTICIPANT_SEARCH_LOAD_MORE_LABEL,
  PARTICIPANT_SEARCH_PLACEHOLDER,
} from '../../../constants/index.js';
import type { DataEntry } from '../../../services/dataHubService.interface.js';
import { DeleteParticipantDataDialog } from '../DeleteParticipantDataDialog/DeleteParticipantDataDialog.js';

import type { ParticipantSearchPanelProps } from './ParticipantSearchPanel.interface.js';
import { useStyles } from './ParticipantSearchPanel.style.js';

const ENTER_KEY = 'Enter';

export function ParticipantSearchPanel({
  dataSetId,
  datasetName,
  service,
  canHardDelete,
  onResultsChange,
  onParticipantDataPurged,
}: ParticipantSearchPanelProps) {
  const styles = useStyles();

  const [email, setEmail] = useState('');
  const [results, setResults] = useState<DataEntry[] | null>(null);
  const [continuationToken, setContinuationToken] = useState<string | null>(
    null,
  );
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const trimmedEmail = email.trim();
  const canSearch = trimmedEmail !== '' && !isSearching;

  const updateResults = useCallback(
    (next: DataEntry[] | null) => {
      setResults(next);
      onResultsChange(next);
    },
    [onResultsChange],
  );

  const runSearch = useCallback(async () => {
    if (trimmedEmail === '') return;
    setIsSearching(true);
    setError(null);
    setContinuationToken(null);
    try {
      const result = await service.searchDataEntriesByParticipant({
        dataSetId,
        emailOrAlias: trimmedEmail,
        includeCount: true,
      });
      updateResults(result.items);
      setContinuationToken(result.continuationToken);
      setTotalCount(result.totalCount);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to search participant data',
      );
    } finally {
      setIsSearching(false);
    }
  }, [service, dataSetId, trimmedEmail, updateResults]);

  const loadMore = useCallback(async () => {
    if (continuationToken === null || trimmedEmail === '') return;
    setIsSearching(true);
    setError(null);
    try {
      const result = await service.searchDataEntriesByParticipant({
        dataSetId,
        emailOrAlias: trimmedEmail,
        includeCount: true,
        continuationToken,
      });
      updateResults([...(results ?? []), ...result.items]);
      setContinuationToken(result.continuationToken);
      setTotalCount(result.totalCount);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to search participant data',
      );
    } finally {
      setIsSearching(false);
    }
  }, [
    service,
    dataSetId,
    trimmedEmail,
    continuationToken,
    results,
    updateResults,
  ]);

  const clear = useCallback(() => {
    updateResults(null);
    setEmail('');
    setContinuationToken(null);
    setTotalCount(null);
    setError(null);
  }, [updateResults]);

  const handleDeleted = useCallback(() => {
    onParticipantDataPurged?.();
  }, [onParticipantDataPurged]);

  const handleDeleteClose = useCallback(() => {
    setIsDeleteOpen(false);
    clear();
  }, [clear]);

  const hasResults = results !== null;
  const resultCountLabel =
    totalCount !== null
      ? `${totalCount} total`
      : `${results?.length ?? 0} result${results?.length === 1 ? '' : 's'}`;

  return (
    <div className={styles.root}>
      <DeleteParticipantDataDialog
        open={isDeleteOpen}
        dataSetId={dataSetId}
        datasetName={datasetName}
        service={service}
        initialEmailOrAlias={trimmedEmail}
        onClose={handleDeleteClose}
        onDeleted={handleDeleted}
      />

      <div className={styles.row}>
        <Input
          className={styles.input}
          value={email}
          onChange={(_, data) => setEmail(data.value)}
          onKeyDown={(event) => {
            if (event.key === ENTER_KEY && canSearch) void runSearch();
          }}
          placeholder={PARTICIPANT_SEARCH_PLACEHOLDER}
          aria-label={PARTICIPANT_SEARCH_PLACEHOLDER}
          disabled={isSearching}
        />
        <Button
          appearance="primary"
          icon={<Search16Regular />}
          disabled={!canSearch}
          onClick={() => {
            void runSearch();
          }}
        >
          {isSearching && !hasResults
            ? PARTICIPANT_SEARCH_BUTTON_BUSY_LABEL
            : PARTICIPANT_SEARCH_BUTTON_LABEL}
        </Button>
        {hasResults && (
          <Button appearance="secondary" onClick={clear}>
            {PARTICIPANT_SEARCH_CLEAR_LABEL}
          </Button>
        )}
      </div>

      {error !== null && (
        <Body1 role="alert" className={styles.count}>
          {error}
        </Body1>
      )}

      {hasResults && (results?.length ?? 0) > 0 && (
        <div className={styles.status}>
          <Text size={200} className={styles.count}>
            {resultCountLabel}
          </Text>
          {canHardDelete && (
            <Button
              appearance="secondary"
              icon={<Delete16Regular />}
              className={styles.hardDeleteButton}
              onClick={() => setIsDeleteOpen(true)}
            >
              {PARTICIPANT_HARD_DELETE_ALL_LABEL}
            </Button>
          )}
          {continuationToken !== null && (
            <Button
              appearance="secondary"
              disabled={isSearching}
              onClick={() => {
                void loadMore();
              }}
            >
              {isSearching
                ? PARTICIPANT_SEARCH_LOAD_MORE_BUSY_LABEL
                : PARTICIPANT_SEARCH_LOAD_MORE_LABEL}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
